import { useEffect, useId, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContainer } from '@adobe/react-spectrum';

import { closeCardInfo } from '../../../data/redux/slices/cardInfoDialogSlice';
import { DynamoConnector } from '../../../data/DynamoConnector';
import { useIsSheetLayout } from '../../../hooks/useIsMobileBreakpoint';
import { buildCardView } from './cardInfoModel';
import { CardReport } from './CardReport';

/**
 * The app-wide card report. Mounted once in App.jsx and opened from anywhere
 * through `useOpenCardInfo()` / the cardInfoDialog slice.
 */
export function CardInfoDialog() {
    const dispatch = useDispatch();
    const openCardId = useSelector((s) => s.cardInfoDialog.openCardId);
    const isLoading = useSelector((s) => s.cardInfoDialog.isLoading);
    const cardData = useSelector((s) => s.cardInfoDialog.cardData);
    const error = useSelector((s) => s.cardInfoDialog.error);
    const seed = useSelector((s) => s.cardInfoDialog.seed);
    const [prints, setPrints] = useState([]);
    const [printsLoading, setPrintsLoading] = useState(false);
    const [selectedFaceId, setSelectedFaceId] = useState(null);
    const isSheet = useIsSheetLayout();
    const titleId = useId();

    // Realign the face whenever the dialog's card changes. `preferredFaceId`
    // is stashed in the seed when the reader clicked a specific face (e.g. the
    // back of a modal card), so the dialog lands on that face.
    useEffect(() => {
        setSelectedFaceId(seed?.preferredFaceId || null);
    }, [openCardId, seed?.preferredFaceId]);

    // Scryfall refreshes pricing daily where our own `price` field can lag, so
    // we pull every printing at once as soon as the card's name is known —
    // from the seed or from the details response.
    useEffect(() => {
        if (!openCardId) {
            setPrints([]);
            setPrintsLoading(false);
            return undefined;
        }
        // The container-level name (the combined name of a double-faced card)
        // keeps a face flip from retriggering the lookup.
        const lookupName = cardData?.name || seed?.name;
        if (!lookupName) return undefined;
        let cancelled = false;
        setPrintsLoading(true);
        DynamoConnector.getScryfallPrints(lookupName, (results) => {
            if (cancelled) return;
            setPrints(Array.isArray(results) ? results : []);
            setPrintsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [openCardId, cardData?.name, seed?.name]);

    const card = useMemo(
        () => buildCardView({ cardData, seed, faceId: selectedFaceId, prints }),
        [cardData, seed, selectedFaceId, prints]
    );

    return (
        <DialogContainer
            type={isSheet ? 'fullscreenTakeover' : 'modal'}
            isDismissable
            onDismiss={() => dispatch(closeCardInfo())}
        >
            {openCardId && (
                <Dialog UNSAFE_className="cardInfoDialog" isDismissable={false} aria-labelledby={titleId}>
                    <CardReport
                        card={card}
                        prints={prints}
                        printsLoading={printsLoading}
                        isLoading={isLoading}
                        error={error}
                        titleId={titleId}
                        onClose={() => dispatch(closeCardInfo())}
                        onFlip={() => setSelectedFaceId(card.nextFaceId)}
                    />
                </Dialog>
            )}
        </DialogContainer>
    );
}
